// Debounced, best-effort item_change trigger. Fires
// livePreviewExecute('item_change') 700ms after the user pauses typing
// in a column that declares a rule/code handler — debounced per row AND
// column, so a multi-row paste cascades every row. Updates returned by
// the backend are dispatched to the caller via ``onUpdates`` so it can
// merge them into its local state.
//
// Both lookup and item_change are non-binding for capture mode: if the
// backend endpoint is unreachable, the deployed function is missing,
// or the server returns an error, we swallow it. The user can still
// edit and save the form; they just don't get the cascade.

import { useCallback, useEffect, useRef } from 'react'

import type { DataAccess } from '../../dataBinding'
import { evaluate, SAFE_FUNCS, DATE_FUNCS } from '../../expr'
import {
  normalizeItemChange,
} from '../types'
import type {
  ColumnDef, SqlModel, ItemChange, ItemChangeResult,
} from '../types'

// Opaque context value the caller threads through trigger → onUpdates.
// Used to route cascaded updates back to the right record / form when
// the trigger fires asynchronously (e.g. `{ recordId: 'r_…' }` for
// the tabular grid or `{ id }` for the popup form).
export type ItemChangeContext = Record<string, unknown>

/** Executor that routes an item_change / row_add / row_edit call to the
 *  hosting app's backend. Injected (same shape as the platform's
 *  `embedExecute` helper) so the package carries no HTTP client of its own. */
export type ItemChangeExecutor = (
  da: DataAccess | undefined, project: string, objName: string,
  action: string, payload?: Record<string, unknown>,
) => Promise<unknown>

export interface UseItemChangeOpts<T extends ItemChangeContext = ItemChangeContext> {
  /** Backend executor (the hosting app's `embedExecute` equivalent). */
  execute: ItemChangeExecutor
  project: string
  objName: string
  sm: SqlModel | null
  /** Merge the server's cascade back into local state. ``updates`` is the
   *  CURRENT form's values (back-compat); ``result`` is the full response
   *  (forms / protect / rows) for callers that apply multi-form / protect /
   *  multi-row changes. */
  onUpdates: (ctx: T, updates: Record<string, unknown>, result?: ItemChangeResult) => void
  debounceMs?: number
  /** Routes the item_change call to the site-txn path in site mode. */
  dataAccess?: DataAccess
}

type ItemChangeResponse = ItemChangeResult

function matchColumn(sm: SqlModel | null, fieldOrDbName: string): ColumnDef | null {
  if (!sm) return null
  const key = (fieldOrDbName || '').trim().toLowerCase()
  if (!key) return null
  for (const w of sm.columns || []) {
    const c = w?.column
    if (!c) continue
    const fkey = (c.name || c.db_name || '').replace(/\s+/g, '_').toLowerCase()
    if (fkey === key) return c
    const dbn = (c.db_name || c.name || '').replace(/\s+/g, '_').toLowerCase()
    if (dbn === key) return c
    if ((c.name || '').toLowerCase() === key) return c
  }
  return null
}

// Form-state key for a column. Headers are keyed by the normalised
// column ``name`` (unique within a form) so dependent fields from
// different joined tables don't collapse onto a single slot when they
// share a db_name (e.g. ``task.descr`` vs ``task_type.descr``).
function formKeyOf(col: ColumnDef): string {
  return (col.name || col.db_name || '').replace(/\s+/g, '_').toLowerCase()
}

export function isInvokableRule(ic: ItemChange | null): boolean {
  if (!ic) return false
  if (ic.mode === 'code') return !!ic.code.trim()
  if (ic.mode === 'rule') {
    // A rule is invokable once it has fills or protect entries. A
    // pure-expression rule has no source_table (the lookup query is
    // skipped server-side).
    return (Array.isArray(ic.fill) && ic.fill.length > 0)
      || (Array.isArray(ic.protect) && ic.protect.length > 0)
  }
  if (ic.mode === 'business_logic') return !!(ic.business_logic?.method || '').trim()
  if (ic.mode === 'rest_api') return !!(ic.rest_api?.url || '').trim()
  return false
}

// Fire item_change once for a single column synchronously (no debounce)
// and return the cascade. Best-effort: returns null on any failure so
// callers can continue without the cascade.
async function fireOnce(
  execute: ItemChangeExecutor,
  da: DataAccess | undefined, project: string, objName: string, col: ColumnDef,
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
  onResult?: (res: ItemChangeResult) => void,
): Promise<Record<string, unknown> | null> {
  try {
    const res = await execute(da, project, objName, 'item_change', {
      field_name: col.name, header,
      // Parent/header-form values (for {header.X} expression fills) and the
      // form_no selecting the line form. Only sent when a parent is in scope.
      ...(headerValues ? { header_values: headerValues } : {}),
      ...(formNo ? { form_no: formNo } : {}),
    }) as ItemChangeResponse
    if (res?.error) {
      if (typeof console !== 'undefined') {
        console.debug('item_change cascade skipped:', col.name, res.error)
      }
      return null
    }
    // Full response (protect / forms) for callers that apply more than the
    // current form's value updates.
    if (res) onResult?.(res)
    return res?.updates || {}
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug('item_change cascade unavailable:', col.name, err)
    }
    return null
  }
}

/**
 * Run item_change cascades for every column in the model that:
 *   - has a non-empty value in ``header`` (i.e. was seeded from a page
 *     input, an authored arg, a literal default, or server-resolved
 *     project variables), AND
 *   - has a configured (non-empty) item_change rule.
 *
 * The cascades run sequentially so each call sees the prior updates.
 * Best-effort throughout — a failed cascade is logged at debug level
 * and skipped, never thrown. Returns the final header with all
 * cascaded updates merged in.
 *
 * Used by addNew so a record seeded with e.g. ``employee_id`` from a
 * page input gets its dependent ``employee_name`` / ``role`` columns
 * populated immediately, the same way they would after a manual edit.
 */
export async function applySeededItemChangeCascades(
  execute: ItemChangeExecutor,
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
  onResult?: (res: ItemChangeResult) => void,
): Promise<Record<string, unknown>> {
  if (!sm || !project || !objName) return header
  let current = { ...header }
  for (const wrap of sm.columns || []) {
    const col = wrap?.column
    if (!col) continue
    const ic = normalizeItemChange(col.item_change)
    if (!isInvokableRule(ic)) continue
    const v = current[formKeyOf(col)]
    if (v == null || v === '') continue
    const updates = await fireOnce(execute, da, project, objName, col, current, headerValues, formNo, onResult)
    if (updates && Object.keys(updates).length > 0) {
      current = { ...current, ...updates }
    }
  }
  return current
}

/**
 * Evaluate the conditional-protect state of a form ONCE for its current
 * values — used when a form OPENS (add / edit), because a column's
 * item_change (where declarative ``protect`` rules live) only fires on
 * *change*. Fires item_change for every column whose rule carries protect
 * entries and a non-empty trigger value, and merges only the returned
 * ``protect`` maps — value updates are deliberately IGNORED so opening a
 * record never rewrites its data.
 *
 * BL-mode handlers that protect conditionally inside code are not detected
 * here (the client can't see into the function) — those should also be wired
 * as a form-level ``row_edit`` / ``row_add`` event, whose protect the caller
 * applies via ``applyRowEvent``'s ``onResult``.
 */
/**
 * Transport-agnostic core of {@link applySeededProtect}: walks the form's
 * columns for rule-mode item_changes that carry protect entries and a
 * non-empty trigger value, fires each through the caller-supplied ``fire``
 * function, and merges only the returned ``protect`` maps. Value updates are
 * deliberately IGNORED so evaluating the lock state never rewrites data.
 * Best-effort: a fire that throws or errors is skipped.
 */
export async function collectSeededProtect(
  sm: SqlModel | null,
  header: Record<string, unknown>,
  fire: (col: ColumnDef, header: Record<string, unknown>) => Promise<ItemChangeResult | null>,
): Promise<Record<string, 0 | 1>> {
  if (!sm) return {}
  const merged: Record<string, 0 | 1> = {}
  for (const wrap of sm.columns || []) {
    const col = wrap?.column
    if (!col) continue
    const ic = normalizeItemChange(col.item_change)
    if (!ic || ic.mode !== 'rule') continue
    if (!Array.isArray(ic.protect) || ic.protect.length === 0) continue
    const v = header[formKeyOf(col)]
    if (v == null || v === '') continue
    try {
      const res = await fire(col, header)
      if (res && !res.error && res.protect) Object.assign(merged, res.protect)
    } catch {
      // Best-effort: keep the form usable without the lock state.
    }
  }
  return merged
}

export async function applySeededProtect(
  execute: ItemChangeExecutor,
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
): Promise<Record<string, 0 | 1>> {
  if (!project || !objName) return {}
  return collectSeededProtect(sm, header, async (col, hdr) => {
    let result: ItemChangeResult | null = null
    await fireOnce(
      execute, da, project, objName, col, hdr, headerValues, formNo,
      (res) => { result = res },
    )
    return result
  })
}

/**
 * Run a FORM-LEVEL row event (``row_add`` / ``row_edit``) once and return the
 * header with the server's values merged in.
 *
 * These are the form-level cousins of ``item_change``: the form carries the
 * same ItemChange definition (rule / code) and the server "sets values based
 * on the definition" — but for the whole row instead of one column. ``row_add``
 * fires when a new blank row is created (right after the seeded item_change
 * cascade), ``row_edit`` when an existing row is opened for edit.
 *
 * Best-effort throughout: a missing definition, an unreachable server, a
 * deployed-function error or a thrown call all return the header unchanged so
 * the user can keep editing.
 */
export async function applyRowEvent(
  execute: ItemChangeExecutor,
  da: DataAccess | undefined,
  project: string, objName: string,
  sm: SqlModel | null,
  which: 'row_add' | 'row_edit',
  header: Record<string, unknown>,
  headerValues?: Record<string, unknown>, formNo?: string,
  onResult?: (res: ItemChangeResult) => void,
): Promise<Record<string, unknown>> {
  if (!sm || !project || !objName) return header
  const ic = normalizeItemChange(which === 'row_add' ? sm.row_add : sm.row_edit)
  if (!isInvokableRule(ic)) return header
  try {
    const res = await execute(da, project, objName, which, {
      header,
      ...(headerValues ? { header_values: headerValues } : {}),
      ...(formNo ? { form_no: formNo } : {}),
    }) as ItemChangeResponse
    if (res?.error) {
      if (typeof console !== 'undefined') {
        console.debug(`${which} cascade skipped:`, res.error)
      }
      return header
    }
    // Full response (protect / forms) for callers that apply more than the
    // value updates.
    if (res) onResult?.(res)
    const updates = res?.updates || {}
    return Object.keys(updates).length > 0 ? { ...header, ...updates } : header
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug(`${which} cascade unavailable:`, err)
    }
    return header
  }
}

/** The values item_change is fired with: a snapshot, or a getter read when the
 *  debounced call actually goes out (so a paste that writes several cells of
 *  one row sends the row as it ends up, not as it was at the first cell). */
export type ItemChangeHeader = Record<string, unknown> | (() => Record<string, unknown>)

/** At most this many item_change calls in flight per grid / form. A 100-row
 *  paste queues its cascades instead of opening 100 requests at once. */
const MAX_IN_FLIGHT = 6

function stableKey(v: unknown): string {
  try { return JSON.stringify(v) ?? '' } catch { return String(v) }
}

// ── Pure-expression rules evaluated in the browser ────────────────────────
// A rule whose fills are ALL expressions over the row / header (`AMOUNT =
// round({form.QTY} * {form.RATE}, 2)`) needs no data the browser lacks, so it
// runs here with the same expression language the engine uses — no 700 ms
// wait per keystroke for arithmetic. Anything else (a lookup, a protect rule,
// a project variable) goes to the server as before; the engine re-derives
// these columns when the record is saved, so this is a preview.
const PLACEHOLDER_RE = /\{(form|header)\.([A-Za-z_][A-Za-z0-9_]*)\}/g

function normName(n: unknown): string {
  return String(n ?? '').trim().replace(/\s+/g, '_').toLowerCase()
}

/** A rule the browser can answer alone: expression fills only. */
export function isPureExpressionRule(ic: ItemChange | null): boolean {
  if (!ic || ic.mode !== 'rule') return false
  const fills = ic.fill || []
  return fills.length > 0 && !(ic.protect || []).length
    && fills.every(f => !(f.table_col || '').trim() && !!(f.expr || '').trim() && !/\{var\./.test(f.expr || ''))
}

export function localExpressionUpdates(
  sm: SqlModel | null, ic: ItemChange | null, values: Record<string, unknown>,
  headerValues?: Record<string, unknown>,
): Record<string, unknown> | null {
  if (!sm || !ic || ic.mode !== 'rule') return null
  const fills = ic.fill || []
  if (!fills.length || (ic.protect || []).length) return null
  if (fills.some(f => (f.table_col || '').trim() || !(f.expr || '').trim() || /\{var\./.test(f.expr || ''))) return null
  const cols = (sm.columns || []).map(w => w?.column).filter(Boolean) as ColumnDef[]
  const keyOf = (n: string): string => {
    const t = normName(n)
    const c = cols.find(x => normName(x.name) === t || normName(x.db_name) === t || formKeyOf(x) === t)
    return c ? formKeyOf(c) : t
  }
  const hv: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(headerValues || {})) hv[normName(k)] = v
  const ctx = { ...values }
  const out: Record<string, unknown> = {}
  try {
    for (const f of fills) {
      const vars: Record<string, unknown> = {}
      let n = 0
      const src = String(f.expr).replace(PLACEHOLDER_RE, (_m, scope: string, name: string) => {
        const v = `__ic${n++}`
        vars[v] = scope === 'form' ? ctx[keyOf(name)] : hv[normName(name)]
        return v
      }).replace(/\bif\s*\(/g, 'iif(')
      let val = evaluate(src, {
        variables: vars, resolveName: (nm) => ctx[keyOf(nm)],
        functions: { ...SAFE_FUNCS, ...DATE_FUNCS },
      })
      if (typeof val === 'number' && Number.isInteger(val)) val = Math.trunc(val)
      const target = keyOf(f.form_col)
      out[target] = val
      ctx[target] = val
    }
  } catch {
    return null
  }
  return out
}

export function useItemChange<T extends ItemChangeContext = ItemChangeContext>(
  opts: UseItemChangeOpts<T>,
) {
  const { execute, project, objName, sm, onUpdates, debounceMs = 700, dataAccess } = opts
  // One debounce timer PER (context, column). A single shared timer meant a
  // paste of 100 item codes — or typing across rows faster than the debounce —
  // cancelled every cascade but the last, leaving the other rows with no
  // name / rate / amount.
  const timersRef = useRef<Map<string, number>>(new Map())
  // Latest call sequence per key: a response that comes back after a newer
  // call for the same cell was issued is stale and dropped.
  const seqRef = useRef<Map<string, number>>(new Map())
  const inFlightRef = useRef(0)
  const queueRef = useRef<Array<() => Promise<void>>>([])
  const onUpdatesRef = useRef(onUpdates)
  onUpdatesRef.current = onUpdates
  const daRef = useRef(dataAccess)
  daRef.current = dataAccess

  useEffect(() => {
    const timers = timersRef.current
    const queue = queueRef.current
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers.clear()
      queue.length = 0
    }
  }, [])

  const pump = useCallback(() => {
    while (inFlightRef.current < MAX_IN_FLIGHT && queueRef.current.length > 0) {
      const job = queueRef.current.shift()!
      inFlightRef.current += 1
      void job().finally(() => {
        inFlightRef.current -= 1
        pump()
      })
    }
  }, [])

  return useCallback((
    ctx: T,
    fieldOrDbName: string,
    header: ItemChangeHeader,
    // Optional parent/header-form values + form_no, supplied by a
    // master-detail caller so {header.X} expression fills resolve. Single-
    // form callers omit them.
    headerValues?: Record<string, unknown>,
    formNo?: string,
  ) => {
    if (!project || !objName) return
    const col = matchColumn(sm, fieldOrDbName)
    if (!col) return
    const ic = normalizeItemChange(col.item_change)
    if (!isInvokableRule(ic)) return

    const key = `${stableKey(ctx)}::${formKeyOf(col)}`
    const seq = (seqRef.current.get(key) || 0) + 1
    seqRef.current.set(key, seq)
    const prev = timersRef.current.get(key)
    if (prev != null) window.clearTimeout(prev)

    // Pure arithmetic over the row: answer in the browser, once the current
    // synchronous burst (a paste writes every cell of a row in one tick) has
    // finished writing the row.
    if (isPureExpressionRule(ic)) {
      queueMicrotask(() => {
        if (seqRef.current.get(key) !== seq) return
        const values = typeof header === 'function' ? header() : header
        const local = localExpressionUpdates(sm, ic, values, headerValues)
        if (local) {
          onUpdatesRef.current(ctx, local, { ok: true, updates: local } as ItemChangeResponse)
          return
        }
        schedule()
      })
      return
    }

    const run = async () => {
      // A newer edit of the same cell superseded this one while it queued.
      if (seqRef.current.get(key) !== seq) return
      try {
        const values = typeof header === 'function' ? header() : header
        const res = await execute(daRef.current, project, objName, 'item_change', {
          field_name: col.name, header: values,
          ...(headerValues ? { header_values: headerValues } : {}),
          ...(formNo ? { form_no: formNo } : {}),
        }) as ItemChangeResponse
        if (seqRef.current.get(key) !== seq) return
        if (res?.error) {
          if (typeof console !== 'undefined') {
            console.debug('item_change skipped:', res.error)
          }
          return
        }
        const updates = res?.updates || {}
        // Fire whenever there's anything to apply — current-form values, a
        // multi-form map, or column protect changes.
        if (Object.keys(updates).length > 0 || res?.forms || res?.protect) {
          onUpdatesRef.current(ctx, updates, res)
        }
      } catch (err) {
        // Best-effort: server / network may be unavailable, the
        // deployed function may be missing — let the user keep editing.
        if (typeof console !== 'undefined') {
          console.debug('item_change unavailable (best-effort):', err)
        }
      }
    }

    function schedule() {
      timersRef.current.set(key, window.setTimeout(() => {
        timersRef.current.delete(key)
        queueRef.current.push(run)
        pump()
      }, debounceMs))
    }
    schedule()
  }, [execute, project, objName, sm, debounceMs, pump])
}
